import { v4 as uuid } from 'uuid';

import { User } from '../../models/User';
import { client } from './client';
import { SizeError } from '../../error/SizeError';
import { logError } from '../../utils/logger';
import { throwIfSupabaseError } from './throwIfSupabaseError';

export async function uploadProfilePicture(
    userId: User['id'],
    file: File,
): Promise<string> {
    return uploadPicture(userId.toString(), file, 'profile-pictures', {
        width: 120,
        height: 120,
        resize: 'cover',
    });
}

export async function uploadContentPicture(file: File): Promise<string> {
    return uploadPicture(
        uuid(),
        file,
        'content-pictures',
        {
            width: 480 * 2,
            height: 270 * 2,
            resize: 'cover',
            quality: 75,
        },
    );
}

async function uploadPicture(
    identifier: string,
    file: File,
    namespace: string,
    transform?: {
        width?: number;
        height?: number;
        resize?: 'contain' | 'cover' | 'fill',
        quality?: number;
    }
): Promise<string> {
    try {
        const name = `public/${namespace}/${identifier}`;

        const response = await client.storage.from('odg-images').upload(name, file, {
            // UUID filenames mean the asset is content-addressed and immutable, so we
            // can let the CDN keep it for a year. `quality` is the only other
            // delivery parameter that ever changes; both live on the URL itself.
            cacheControl: '31536000',
            upsert: true,
        });
        throwIfSupabaseError(response);

        const publicData = client.storage.from('odg-images').getPublicUrl(response.data.path, transform ? {
            transform,
        } : undefined);

        return publicData.data.publicUrl;
    } catch (error) {
        if (isSizeTooLarge(error)) {
            throw new SizeError('Slika je veća od 2MB. Odaberite manju sliku.');
        }

        logError(error);
        throw error;
    }
}

export function isSizeTooLarge(error: unknown) {
    if (!error || typeof error !== 'object' || !('statusCode' in error)) {
        return false;
    }

    return String((error as { statusCode: unknown }).statusCode) === '413';
}
